/**
 * @fileoverview Public-tools strings barrel — the EN/ES bundles plus
 *   `loadPublicToolsStrings(i18nInstance, locales)`, which registers them under the
 *   `publicTools` namespace on a react-i18next instance.
 * @package @puffin-core/public-tools
 *
 * ## 🔴 LEAF-ONLY by design — this subpath must not import a component
 *
 * `@puffin-core/i18n-namespaces` imports this module at APP BOOT from every web app AND from
 * every public marketing portal (the `/marketing` registry), so anything reachable from here lands
 * in every boot chunk — a public page's included. It imports only its own frozen data modules and
 * a zero-import namespace constant.
 *
 * @module @puffin-core/public-tools/strings
 */

import { PUBLIC_TOOLS_EN } from './en.js';
import { PUBLIC_TOOLS_ES } from './es.js';
import { PUBLIC_TOOLS_NAMESPACE } from './namespace.js';

export { PUBLIC_TOOLS_EN, PUBLIC_TOOLS_ES, PUBLIC_TOOLS_NAMESPACE };

const BUNDLES = Object.freeze({ en: PUBLIC_TOOLS_EN, es: PUBLIC_TOOLS_ES });

/**
 * The locales this package ships bundles for — DERIVED from `BUNDLES`, never typed out (a restated
 * list is a second copy of a fact, ENUM-DRIFT-001).
 *
 * @constant {ReadonlyArray<string>}
 */
export const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

/**
 * Register one or more public-tools bundles on a react-i18next instance.
 *
 * @param {object} i18nInstance - The react-i18next instance.
 * @param {string|string[]} [locales='en'] - Locale code(s) to load.
 * @returns {object} The same instance, for chaining.
 * @throws {Error} If the instance lacks `addResourceBundle`, or a locale is unsupported.
 */
export function loadPublicToolsStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error(
      'loadPublicToolsStrings: expected a react-i18next instance with addResourceBundle()',
    );
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadPublicToolsStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // Deep merge so an app may add keys; never overwrite, so a caller-defined value wins.
    i18nInstance.addResourceBundle(locale, PUBLIC_TOOLS_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}

export { resolveString } from './resolveString.js';
